import axios from "axios";
import appConfig from "../../../configs/app.config";
import { invokeOmsService } from "./apiService";

// Function to get SAS token for file upload
export const getSasToken = async (requestBody) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.uploads.getSasToken,
    body: requestBody,
  };
  return await invokeOmsService(requestParams);
};

export const getSasTokenSignature = async (requestBody) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.signature.getSasTokenSignature,
    body: requestBody,
  };
  return await invokeOmsService(requestParams);
};

// Function to upload file using SAS token (original method using axios)
export const uploadFileWithSas = async (file, sasUrl) => {
  try {
    const response = await axios.put(sasUrl, file, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": file.type,
      },
    });
    return response;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

// Function to upload signature/media files using SAS token (uses fetch to avoid URL encoding issues)
export const uploadSignatureWithSas = async (file, sasUrl) => {
  try {
    // Use fetch instead of axios to avoid URL encoding issues with SAS token
    // Azure SAS tokens are very sensitive to URL encoding - must use URL as-is
    const fileBlob = file instanceof Blob ? file : new Blob([file], { type: file.type });
    
    const response = await fetch(sasUrl, {
      method: 'PUT',
      body: fileBlob,
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": file.type || "application/octet-stream",
        "Content-Length": file.size.toString(),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure upload failed: ${response.status} ${response.statusText}. ${errorText}`);
    }

    // Return response in similar format to axios for compatibility
    return {
      status: response.status,
      statusText: response.statusText,
      data: null,
    };
  } catch (error) {
    console.error("Error uploading signature/media file:", error);
    console.error("SAS URL used:", sasUrl);
    console.error("File details:", { name: file.name, size: file.size, type: file.type });
    throw error;
  }
};

// Function to remove uploaded file
export const removeUploadedFile = async (orderId) => {
  const requestParams = {
    method: "STRICT_DELETE",
    url: appConfig.v2.uploads.removeUploadedFile.replace("{orderId}", orderId),
  };
  return await invokeOmsService(requestParams);
};

export const getSasTokenLogo = async (requestBody) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.uploads.getSaSTokenLogo,
    body: requestBody,
  };
  return await invokeOmsService(requestParams);
};

export const getHipaaSasToken = async (requestBody) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.uploads.getHipaaSasToken,
    body: requestBody,
  };
  return await invokeOmsService(requestParams);
};


export const getAdminHipaaSasToken = async (requestBody) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.uploads.getAdminHipaaSasToken,
    body: requestBody,
  };
  return await invokeOmsService(requestParams);
};

export const getAdminSasTokenLogo = async (requestBody) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.uploads.getAdminSaSTokenLogo,
    body: requestBody,
  };
  return await invokeOmsService(requestParams);
};

export const removeFileInAzureByPath = async (body) => {
  const requestParams = {
    method: "STRICT_DELETE",
    url: appConfig.v2.uploads.deleteAzureFile,
    body
  };
  return await invokeOmsService(requestParams);
};

export const getDeclarationFile = async (orderId) => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.uploads.getDeclarationFile.replace("{orderId}", orderId),
  };
  return await invokeOmsService(requestParams);
};
// Main upload function
export const uploadFile = async (file, orderId, type, recordId, view) => {
  try {
    const requestBody = {
      fileName: file.name,
      type,
      orderId,
      recordId,
      view,
    };
    const response = await getSasToken(requestBody);

    if (response.data.success) {
      // Get SAS token and blob URL
      const { sasUrl, blobName } = response.data.data;

      // Upload file
      await uploadFileWithSas(file, sasUrl);
      return { success: true, fileUrl: sasUrl, path: blobName};
    }
  } catch (error) {
    return { success: false, error };
  }
};
